import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { apiFetchWithTimeout } from "../lib/api-client";
import { LiveSmsConversation } from "./Communications";
import { formatCommunicationPhone } from "./communications-model";
import type { TechnicianTextTarget } from "./RecordCommunications";

export function TechnicianTextPanel({ technician, timezone, onClose }: { technician: TechnicianTextTarget; timezone: string; onClose: () => void }) {
  const panel = useRef<HTMLElement>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.focus();
    return () => { if (document.activeElement === document.body) previous?.focus(); };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    void apiFetchWithTimeout(`/api/v1/workspace/communications/technicians/${encodeURIComponent(technician.id)}/sms`, { method: "POST", signal: controller.signal })
      .then(async response => {
        const payload = await response.json() as { data?: { id: string }; error?: { message?: string } };
        if (!response.ok || !payload.data?.id) throw new Error(response.status === 404 ? "Technician texting is not available yet. The API update is required." : payload.error?.message || "This technician's texts could not be opened.");
        if (!controller.signal.aborted) setConversationId(payload.data.id);
      }).catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Texts could not be opened."); });
    return () => controller.abort();
  }, [technician.id, revision]);
  return createPortal(<section ref={panel} tabIndex={-1} className="comms-texts-popover comms-technician-panel" role="dialog" aria-modal="false" aria-label={`Text ${technician.name}`} onKeyDown={event => {
    if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); onClose(); }
  }}>
    <header className="comms-texts-page-header"><div><h1>Text {technician.name}</h1><p>{formatCommunicationPhone(technician.phone)}</p></div><button type="button" className="comms-icon-button" aria-label="Close technician texts" onClick={onClose}><X size={20} /></button></header>
    <div className="comms-text-feed comms-technician-feed">
      {error ? <p role="alert" className="comms-review-error">{error} <button type="button" className="comms-link" onClick={() => setRevision(value => value + 1)}>Try again</button></p>
        : conversationId ? <LiveSmsConversation key={conversationId} id={conversationId} timezone={timezone} canReply /> : <p role="status" className="comms-muted">Opening technician texts…</p>}
    </div>
  </section>, document.body);
}
